/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Rocket, Check, User, Mail, Building, Users, FlaskConical, Lock, LogIn, AlertCircle, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import MessageModal from '../components/common/MessageModal';
import PasswordRequirements from '../components/common/PasswordRequirements';
import { dbService, User as UserType, supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import {
  validateEmail,
  validatePassword,
  validateUsername,
  validateName,
  validateCompany,
  validateJobRole,
  validateUrl,
  validateEmailDomainMatch,
} from '../utils/validations';
import Dompurify from 'dompurify';
import { storePendingVerificationEmail } from '../utils/sessionStorage';
import { getAppUrl } from '../utils/appUrl';
import { getSafeRedirectFromLocationState } from '../utils/safeRedirect';
import { SELECTED_TILE_CLASS } from '../constants';
import { VALIDATION_MESSAGES } from '../constants/messages';
import { SPONSOR_INVITE_QUERY_PARAM } from '../constants/sponsorInviteCampaign';

// Ensure all user input is sanitized to prevent XSS attacks
const sanitizeInput = (input: string) => {
  return Dompurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

const StartupSignup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading, refreshAuth } = useAuth();
  const [isLogin, setIsLogin] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    role: 'startup',
    company: '',
    jobRole: '',
    website: '',
    developmentStage: [] as string[],
    lookingFor: [] as string[],
    challenge: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [disabledFields, setDisabledFields] = useState<{ email: boolean; role: boolean; website: boolean }>({ email: false, role: false, website: false });
  /**
   * On a claim link: the contact emails saved on the profile (lower case). The bulk upload may save a
   * subsidiary's address (PHAR-485), so such an address may sign up even when its domain differs from
   * the profile's website; any other email must still be on the website's domain.
   */
  const [claimContactEmails, setClaimContactEmails] = useState<string[]>([]);
  const [transferEmailMismatch, setTransferEmailMismatch] = useState(false);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [transferInfo, setTransferInfo] = useState<{
    isTransfer: boolean;
    canonicalEmail?: string;
    cdmoId?: string;
    company?: string;
    website?: string;
    transferToken?: string;
  }>({ isTransfer: false });

  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  const showMessageModal = (title: string, message: string, type: 'error' | 'success' | 'warning' = 'error') => {
    setMessageModal({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const stages = [
    'Preclinical',
    'Phase I',
    'Phase II',
    'Phase III',
    'FDA Approval',
    'Commercial'
  ];

  const services = [
    'Drug substance / API manufacturing',
    'Formulation development',
    'Drug product manufacturing',
    'Sterile fill-finish',
    'Analytical testing',
    'Packaging / assembly',
    'Clinical manufacturing',
    'Commercial manufacturing',
    'Regulatory / quality support',
    'Not sure yet'
  ];

  // Handle URL parameters, claim token, transfer token, and existing user redirects
  useEffect(() => {
    let isMounted = true;

    const handleTokens = async () => {
      const token = searchParams.get('token');
      const transferToken = searchParams.get('transfer_token');
      const sponsorInviteToken = searchParams.get(SPONSOR_INVITE_QUERY_PARAM);
      const paramEmail = searchParams.get('email')?.toLowerCase().trim();
      const cdmoId = searchParams.get('id');

      if (sponsorInviteToken) {
        try {
          const invite = await dbService.getSponsorInviteByToken(sponsorInviteToken);
          if (!isMounted) return;

          if (!invite) {
            setSubmissionError('This invitation link is invalid or has expired.');
            showMessageModal(
              VALIDATION_MESSAGES.MODAL_TITLES.LINK_PROBLEM,
              'This invitation link is invalid or has expired.',
              'error',
            );
            return;
          }

          if (invite.converted_user_id) {
            setSubmissionError('This invitation has already been used.');
            showMessageModal(
              VALIDATION_MESSAGES.MODAL_TITLES.LINK_PROBLEM,
              'This invitation has already been used.',
              'warning',
            );
            return;
          }

          setFormData((prev) => ({
            ...prev,
            role: 'startup',
          }));
          setDisabledFields({ email: false, role: true, website: false });
        } catch (error) {
          if (isMounted) {
            console.error('Error validating sponsor invite token:', error);
          }
        }
        return;
      }

      // Handle transfer token: fetch canonical transfer data from ownership transfers
      // When user is already logged in, skip this block so we don't show "link expired" after
      // they just completed the transfer via login (completed transfers are hidden by RLS).
      if (transferToken) {
        if (user) return;

        try {
          const transfer = await dbService.getOwnershipTransferByToken(transferToken);

          if (!isMounted) return;

          if (!transfer) {
            setSubmissionError(VALIDATION_MESSAGES.TRANSFER_LINK_INVALID);
            showMessageModal(VALIDATION_MESSAGES.MODAL_TITLES.LINK_PROBLEM, VALIDATION_MESSAGES.TRANSFER_LINK_INVALID, 'error');
            return;
          }

          const cdmoApp = transfer?.cdmo_applications || null;
          const canonicalEmail = transfer?.receiver_email?.toLowerCase().trim();
          const cdmoAppId = transfer?.cdmo_id || cdmoApp?.id;
          const companyName = cdmoApp?.company_name || '';
          const website = cdmoApp?.website || '';

          // Check if the CDMO is already transferred to someone else 
          // (i.e. if the current owner is no longer the sender of this transfer)
          if (cdmoApp && cdmoApp?.user_id && cdmoApp?.user_id !== transfer.sender_id) {
            setAlreadyClaimed(true);

            // If visitor is already logged in as the new owner, just redirect them
            if (user && cdmoApp?.user_id === user.id) {
              navigate('/cdmo-dashboard', { replace: true });
              return;
            }

            let msg = VALIDATION_MESSAGES.LINK_ALREADY_CLAIMED;
            let title = VALIDATION_MESSAGES.LINK_ALREADY_CLAIMED_TITLE;

            setSubmissionError(msg);
            showMessageModal(title, msg, 'warning');
            return;
          }

          // Check if this specific transfer is already completed or expired
          if (transfer.status === 'completed') {
            setSubmissionError(VALIDATION_MESSAGES.TRANSFER_ALREADY_COMPLETED);
            showMessageModal(VALIDATION_MESSAGES.MODAL_TITLES.ALREADY_CLAIMED, VALIDATION_MESSAGES.TRANSFER_ALREADY_COMPLETED, 'warning');
            return;
          }

          if (transfer.status === 'expired') {
            setSubmissionError(VALIDATION_MESSAGES.TRANSFER_LINK_EXPIRED);
            showMessageModal(VALIDATION_MESSAGES.MODAL_TITLES.LINK_EXPIRED, VALIDATION_MESSAGES.TRANSFER_LINK_EXPIRED, 'error');
            return;
          }

          // Pre-fill the form using canonical server-side values
          setFormData(prev => ({
            ...prev,
            role: 'cdmo',
            email: canonicalEmail || (paramEmail || ''),
            company: companyName || prev.company,
            website: website || prev.website
          }));

          setTransferInfo({
            isTransfer: true,
            canonicalEmail: canonicalEmail,
            cdmoId: cdmoAppId,
            company: companyName,
            website,
            transferToken
          });

          // Lock email and role for transfers - backend will validate email matches transfer recipient
          setDisabledFields({ email: true, role: true, website: true });

          // Initialize mismatch state
          setTransferEmailMismatch(false);

          if (!isMounted) return;
        } catch (error) {
          if (isMounted) {
            console.error('Error handling transfer token:', error);
            setSubmissionError('Invalid or expired transfer link.');
          }
        }
        return;
      }

      // Handle regular claim token
      if (!token) return;

      /** Records claim-campaign CTA click (also set via /campaign-click when email uses that URL). */
      const recordClaimCampaignClick = async (id: string) => {
        try {
          await dbService.recordCdmoClaimLinkClick(id);
        } catch (clickErr) {
          console.warn('recordCdmoClaimLinkClick:', clickErr);
        }
      };

      if (cdmoId) {
        await recordClaimCampaignClick(cdmoId);
      }

      try {
        const app = await dbService.getCDMOApplicationByToken(token);

        if (!isMounted) return;

        if (app?.id && !cdmoId) {
          await recordClaimCampaignClick(app.id);
        }

        if (!app) {
          // Token lookup failed - the link might be already claimed or simply invalid.
          if (cdmoId) {
            try {
              // Fetch the public CDMO record to check its internal status/owner
              const appById = await dbService.getCDMOApplicationById(cdmoId);

              if (appById && (appById.status === 'claimed' || appById.user_id)) {
                // If visitor is already logged in as the owner, just redirect them
                if (user && appById.user_id === user.id) {
                  navigate('/cdmo-dashboard', { replace: true });
                  return;
                }

                setAlreadyClaimed(true);

                // Show "Link already claimed" when someone reuses the same claim link after it was used
                const msg = VALIDATION_MESSAGES.LINK_ALREADY_CLAIMED;
                const title = VALIDATION_MESSAGES.LINK_ALREADY_CLAIMED_TITLE;

                setSubmissionError(msg);
                showMessageModal(title, msg, 'warning');
                return;
              }
            } catch (idErr) {
              console.warn('Error checking CDMO by ID:', idErr);
            }
          }

          // If no app found by token and it's not already claimed, it must be expired/invalid
          setSubmissionError(VALIDATION_MESSAGES.CLAIM_TOKEN_EXPIRED);
          showMessageModal(VALIDATION_MESSAGES.MODAL_TITLES.LINK_PROBLEM, VALIDATION_MESSAGES.CLAIM_TOKEN_EXPIRED, 'error');
          return;
        }

        let targetEmail = '';
        let shouldDisableEmail = false;

        // Priority 1: If admin created this profile, it is strictly tied to the email in the DB.
        if (app.is_admin_created && app.email) {
          targetEmail = app.email;
          shouldDisableEmail = true;
        }
        // Priority 2: If URL has a specific email, use it
        else if (paramEmail) {
          targetEmail = paramEmail;
          shouldDisableEmail = true;
        } else {
          // Do not autofill the email as a hint when claim token is present
          targetEmail = '';
          shouldDisableEmail = false;
        }

        setFormData(prev => ({
          ...prev,
          role: 'cdmo',
          email: targetEmail,
          company: app.company_name || '',
          website: app.website || ''
        }));

        // Only lock email if we are sure which one it is
        setDisabledFields({ email: shouldDisableEmail, role: true, website: true });
        setClaimContactEmails(
          [app.email, app.primary_email, ...(app.secondary_emails ?? [])]
            .map((address) => (address ?? '').trim().toLowerCase())
            .filter(Boolean)
        );

      } catch (error) {
        if (isMounted) {
          console.error('Error fetching CDMO application by token:', error);
        }
      }
    };

    handleTokens();

    return () => {
      isMounted = false;
    };
  }, [searchParams, user, navigate]);


  useEffect(() => {
    const handleLoggedInUserRedirect = async () => {
      if (!user?.id) return;

      try {
        // Get user profile to check role and email
        const { data: userProfile } = await supabase
          .from('users')
          .select('role, email')
          .eq('id', user.id)
          .single();

        if (userProfile?.role === 'cdmo') {
          // Check if user is admin first
          let isUserAdmin = false;
          try {
            isUserAdmin = await dbService.isUserAdmin(userProfile.email || '');
          } catch (adminError) {
            console.warn('Admin check failed:', adminError);
          }

          // Admins bypass the intake form and go directly to dashboard
          if (isUserAdmin) {
            navigate('/cdmo-dashboard', { replace: true });
          } else {
            // Regular CDMO users need to complete their profile
            const profileStatus = await dbService.checkCDMOProfileCompletion(user.id);

            if (!profileStatus.hasProfile || !profileStatus.isComplete) {
              navigate('/cdmo-application', { replace: true });
            } else {
              navigate('/cdmo-dashboard', { replace: true });
            }
          }
        } else {
          navigate('/matchmaking', { replace: true });
        }
      } catch (error) {
        console.error('Error checking user profile for redirect:', error);
        navigate('/matchmaking', { replace: true });
      }
    };

    // Handle query parameters
    const mode = searchParams.get('mode');
    const message = searchParams.get('message');

    if (mode === 'login') {
      setIsLogin(true);
    }

    if (message === 'registration_success') {
      // Show success message for completed registration
      setTimeout(() => {
        setSubmissionError(null);
        setSubmitted(false);
      }, 100);
    }

    // Only redirect logged-in users if they're not in login mode (to prevent disrupting the login process)
    // Wait for loading to complete before redirecting
    // IMPORTANT: Skip auto-redirect when a claim/transfer token is present — the login handler
    // processes the token first (updates role to cdmo), then redirects. If we let the effect
    // redirect here, it fires with the OLD startup role before the token is processed.
    const hasClaimToken =
      searchParams.get('token') ||
      searchParams.get('transfer_token') ||
      searchParams.get(SPONSOR_INVITE_QUERY_PARAM);
    if (!loading && user && !mode && !hasClaimToken) {
      // User is already logged in and just visiting the signup page directly
      // Perform role-based redirect
      handleLoggedInUserRedirect();
    }
  }, [user, loading, navigate, searchParams]);

  // Real-time field validation
  const validateField = (name: string, value: string) => {
    let validation;

    switch (name) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'username':
        validation = validateUsername(value);
        break;
      case 'password':
        validation = validatePassword(value);
        break;
      case 'company':
        validation = validateCompany(value);
        break;
      case 'jobRole':
        validation = validateJobRole(value);
        break;
      case 'website':
        validation = validateUrl(value, true);
        if (validation.isValid && value && formData.email) {
          const domainMatch = validateEmailDomainMatch(formData.email, value);
          if (!domainMatch.isValid) validation = domainMatch;
        }
        break;
      default:
        validation = { isValid: true };
    }

    setValidationErrors(prev => ({
      ...prev,
      [name]: validation.isValid ? '' : (validation.error || 'Invalid value')
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear submission error when user starts typing
    setSubmissionError(null);

    // If this is a transfer flow, and the user edits the email, validate against canonical transfer email
    if (name === 'email' && transferInfo?.isTransfer && transferInfo.canonicalEmail) {
      const entered = value.toLowerCase().trim();
      const canonical = transferInfo.canonicalEmail.toLowerCase().trim();
      if (entered !== canonical) {
        setValidationErrors(prev => ({ ...prev, email: VALIDATION_MESSAGES.EMAIL_TRANSFER_MISMATCH(canonical) }));
        setTransferEmailMismatch(true);
      } else {
        setValidationErrors(prev => ({ ...prev, email: '' }));
        setTransferEmailMismatch(false);
      }
      return;
    }

    // Real-time validation for relevant fields
    if (!isLogin || name === 'email' || name === 'password') {
      validateField(name, value);
    }
  };

  const handleCheckboxChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      lookingFor: prev?.lookingFor?.includes(service)
        ? prev?.lookingFor?.filter(s => s !== service)
        : [...(prev?.lookingFor || []), service]
    }));
  };

  const handleDevelopmentStageChange = (stage: string) => {
    setFormData(prev => ({
      ...prev,
      developmentStage: prev.developmentStage.includes(stage)
        ? prev.developmentStage.filter(s => s !== stage)
        : [...prev.developmentStage, stage]
    }));
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50"
        >
          <div className="w-12 h-12 border-4 border-electric-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-charcoal mb-2">Checking Authentication</h2>
          <p className="text-slate-gray">Please wait...</p>
        </motion.div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError(null);
    setValidationErrors({});

    try {
      if (isLogin) {
        // Login existing user - validate email and password
        validateField('email', formData.email);

        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email: sanitizeInput(formData.email),
          password: formData.password,
        });

        if (authError) {
          // Handle the specific case where email is not confirmed
          if (authError.message === 'Email not confirmed') {
            // Store email in session storage instead of URL
            storePendingVerificationEmail(sanitizeInput(formData.email));
            navigate('/verify-email?from=login');
            return;
          } else {
            // Other auth errors
            throw authError;
          }
        }

        if (authData.session) {
          // Check if email is verified
          if (!authData.user?.email_confirmed_at) {
            // User exists in auth but email is not verified
            // Check if user exists in custom users table (they signed up but didn't verify)
            try {
              const userProfile = await dbService.getUserProfile(authData.user?.id || '');
              if (userProfile) {
                // User exists in custom table but email not verified
                // Redirect to verification page with resend option
                navigate('/verify-email?from=login');
                return;
              } else {
                // User doesn't exist in custom table, something went wrong
                throw new Error('User profile not found. Please sign up again.');
              }
            } catch (error) {
              console.error('Error checking user profile:', error);
              setSubmissionError('There was an issue with your account. Please try signing up again.');
              return;
            }
          }

          // Perform ALL checks synchronously BEFORE any redirect
          try {
            const userId = authData.user?.id || '';

            // ── Step 1: Process any claim/transfer token FIRST ──────────────────
            // This updates the user's role to 'cdmo' in the DB before we decide
            // where to redirect. Order matters: token → role update → fresh fetch → redirect.
            const token = searchParams.get('token');
            const transferToken = searchParams.get('transfer_token');

            if (userId && formData.email) {
              try {
                if (transferToken) {
                  const transfer = await dbService.getOwnershipTransferByToken(transferToken);
                  if (transfer && transfer.status === 'pending') {
                    await dbService.completeOwnershipTransfer(
                      transferToken,
                      userId,
                      sanitizeInput(formData.email)
                    );
                    console.log('✅ Ownership transfer completed during login');
                    // Update auth metadata so the JWT reflects the new CDMO role
                    await supabase.auth.updateUser({ data: { role: 'cdmo' } });
                    if (refreshAuth) await refreshAuth();
                  }
                } else if (token) {
                  const app = await dbService.getCDMOApplicationByToken(token);
                  if (app && !app.user_id) {
                    await dbService.claimCDMOApplication(userId, sanitizeInput(formData.email), token);
                    console.log('✅ CDMO application claimed during login');
                    // Update auth metadata so the JWT reflects the new CDMO role
                    await supabase.auth.updateUser({ data: { role: 'cdmo' } });
                    if (refreshAuth) await refreshAuth();
                  }
                }
              } catch (tokenError) {
                // Non-fatal: log the error but still proceed with the normal login redirect.
                // Surface it via toast (survives the redirect below) instead of silently
                // landing the user on a blank CDMO application with no explanation.
                console.warn('⚠️ Error processing token during login:', tokenError);
                toast.error(tokenError instanceof Error ? tokenError.message : VALIDATION_MESSAGES.TRANSACTION_ERROR);
              }
            }

            // ── Step 2: Fetch the FRESH user profile after any role updates ──────
            const { data: userProfile } = await supabase
              .from('users')
              .select('role, email')
              .eq('id', userId)
              .single();

            // ── Step 3: Check admin status ───────────────────────────────────────
            let isUserAdmin = false;
            try {
              isUserAdmin = await dbService.isUserAdmin(userProfile?.email || '');
            } catch (adminError) {
              console.warn('Admin check failed (table may not exist yet):', adminError);
            }

            // ── Step 4: Decide redirect path based on fresh role ─────────────────
            let redirectPath = '/matchmaking'; // Default redirect

            if (userProfile?.role === 'cdmo') {
              if (isUserAdmin) {
                redirectPath = '/cdmo-dashboard';
              } else {
                const profileStatus = await dbService.checkCDMOProfileCompletion(userId);
                if (!profileStatus.hasProfile || !profileStatus.isComplete) {
                  redirectPath = '/cdmo-application';
                } else {
                  redirectPath = '/cdmo-dashboard';
                }
              }
            }

            // If the user was bounced here from a specific protected page (e.g. an
            // email "Go to Dashboard" link), return them there instead of the
            // role-based default — this is the whole point of capturing it
            // (client-reported issue #5-2: links landed on the homepage instead).
            const requestedPath = getSafeRedirectFromLocationState(location.state);
            if (requestedPath) {
              redirectPath = requestedPath;
            }

            navigate(redirectPath, { replace: true });
          } catch (error) {
            console.error('Error in login redirect flow:', error);
            navigate('/matchmaking', { replace: true });
          }
        }
      } else {
        // Sign up new user - full validation
        const validationErrors: string[] = [];

        // Validate all fields
        const nameValidation = validateName(formData.name);
        if (!nameValidation.isValid) validationErrors.push(nameValidation.error!);

        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) validationErrors.push(emailValidation.error!);

        const usernameValidation = validateUsername(formData.username);
        if (!usernameValidation.isValid) validationErrors.push(usernameValidation.error!);

        const passwordValidation = validatePassword(formData.password);
        if (!passwordValidation.isValid) validationErrors.push(passwordValidation.error!);

        const companyValidation = validateCompany(formData.company);
        if (!companyValidation.isValid) validationErrors.push(companyValidation.error!);

        const jobRoleValidation = validateJobRole(formData.jobRole);
        if (!jobRoleValidation.isValid) validationErrors.push(jobRoleValidation.error!);

        if (formData.role === 'cdmo') {
          const websiteValidation = validateUrl(formData.website, true);
          if (!websiteValidation.isValid) {
            validationErrors.push(websiteValidation.error!);
          } else if (formData.email && !claimContactEmails.includes(formData.email.trim().toLowerCase())) {
            const domainMatch = validateEmailDomainMatch(formData.email, formData.website);
            if (!domainMatch.isValid) {
              validationErrors.push(domainMatch.error!);
            }
          }
        }

        if (validationErrors.length > 0) {
          throw new Error(`${VALIDATION_MESSAGES.FIX_FOLLOWING_ERRORS}${validationErrors.join(', ')}`);
        }

        // If this is a transfer flow and the email was changed to something that doesn't match canonical,
        // block submission and surface a helpful message.
        if (transferInfo?.isTransfer && transferEmailMismatch) {
          throw new Error(VALIDATION_MESSAGES.EMAIL_TRANSFER_MISMATCH(transferInfo.canonicalEmail || ''));
        }
        // Check if the email address already has a registered account when dealing with a claim token
        const tokenParam = searchParams.get('token');
        if (tokenParam && formData.role === 'cdmo') {
          const { data: existingProfile } = await supabase
            .from('cdmo_applications')
            .select('id')
            .eq('email', sanitizeInput(formData.email).toLowerCase())
            .not('user_id', 'is', null)

          if (existingProfile && existingProfile.length > 0) {
            throw new Error(VALIDATION_MESSAGES.EMAIL_ALREADY_ASSOCIATED_WITH_CLAIMED_PROFILE);
          }
        }

        // Create a simple redirect URL for email confirmation
        const appUrl = getAppUrl();
        const emailConfirmUrl = `${appUrl}/confirm-email`;

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: sanitizeInput(formData.email),
          password: formData.password,
          options: {
            data: {
              username: sanitizeInput(formData.username)
            },
            emailRedirectTo: emailConfirmUrl
          }
        });

        if (authError) {
          throw authError;
        }

        if (!authData.user) {
          throw new Error('Failed to create user account');
        }

        // Step 2: Always create user profile regardless of session status
        // This ensures the user exists in our custom users table even if email confirmation is required
        const userProfileData: UserType = {
          id: authData.user.id,
          name: sanitizeInput(formData.name),
          email: sanitizeInput(formData.email),
          username: sanitizeInput(formData.username),
          company: sanitizeInput(formData.company),
          role: formData?.role, // startup | cdmo
          job_role: sanitizeInput(formData.jobRole),

          development_stage:
            formData.role === 'startup'
              ? formData.developmentStage.map(sanitizeInput)
              : [],

          looking_for:
            formData.role === 'startup'
              ? formData.lookingFor.map(sanitizeInput)
              : [],

          challenge:
            formData.role === 'startup'
              ? sanitizeInput(formData.challenge) || undefined
              : undefined,
          website:
            formData.role === 'cdmo'
              ? sanitizeInput(formData.website)
              : undefined,
        };


        await dbService.createUserProfile(userProfileData);

        const sponsorInviteTokenParam = searchParams.get(SPONSOR_INVITE_QUERY_PARAM);
        if (sponsorInviteTokenParam && formData.role === 'startup') {
          try {
            await dbService.completeSponsorInviteConversion(
              sponsorInviteTokenParam,
              authData.user.id,
              sanitizeInput(formData.email),
            );
          } catch (inviteErr) {
            console.warn('Sponsor invite conversion tracking failed:', inviteErr);
          }
        }

        // Handle CDMO transfer/claim logic during sign up
        const transferTokenParam = searchParams.get('transfer_token');

        try {
          if (transferTokenParam && formData.role === 'cdmo') {
            // Handle ownership transfer - mark as transferred
            const transfer = await dbService.getOwnershipTransferByToken(transferTokenParam);
            if (transfer && transfer.status === 'pending') {
              await dbService.completeOwnershipTransfer(
                transferTokenParam,
                authData.user.id,
                sanitizeInput(formData.email)
              );
              console.log('Ownership transfer completed during sign up');
            }
          } else if (tokenParam && formData.role === 'cdmo') {
            // Handle regular CDMO claim
            const app = await dbService.getCDMOApplicationByToken(tokenParam);
            if (app && !app.user_id && app.id) {
              await dbService.claimCDMOApplication(authData.user.id, sanitizeInput(formData.email), tokenParam);
              console.log('CDMO application claimed during sign up');
            }
          }
        } catch (transferError) {
          console.warn('Transfer/claim logic during sign up had an issue:', transferError);
          // Don't block signup if transfer fails - it can be handled later. But do tell the
          // user via toast (survives the redirect below) instead of silently landing them on
          // a blank CDMO application with no explanation (client-reported bug).
          toast.error(transferError instanceof Error ? transferError.message : VALIDATION_MESSAGES.TRANSACTION_ERROR);
        }

        setSubmitted(true);

        // After successful registration, redirect to verify email
        setTimeout(() => {
          if (authData.session) {
            // If user has immediate session, redirect to login to trigger role-based flow
            navigate('/startup-signup?mode=login&message=registration_success');
          } else {
            // User needs to confirm email before accessing protected routes
            try {
              storePendingVerificationEmail(sanitizeInput(formData.email));
            } catch (e) {
              console.warn('Failed to store pending verification email:', e);
            }
            navigate('/verify-email?from=signup');
          }
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error submitting startup signup:', error);

      // Type guard for error object
      const err = error as { message?: string; code?: string };

      // Enhanced error handling with validation-specific messages
      if (err?.message?.includes('Invalid login credentials')) {
        setSubmissionError(VALIDATION_MESSAGES.INVALID_LOGIN_CREDENTIALS);
      } else if (err?.message?.includes('User already registered')) {
        const token = searchParams.get('token') || searchParams.get('transfer_token');
        if (token) {
          setSubmissionError(VALIDATION_MESSAGES.EMAIL_ALREADY_REGISTERED_FOR_CLAIM);
          setIsLogin(true); // Automatically switch to login mode
        } else {
          setSubmissionError(VALIDATION_MESSAGES.EMAIL_ALREADY_REGISTERED_TO_CDMO);
        }
      } else if (err?.message?.includes('Password should be at least 6 characters')) {
        setSubmissionError(VALIDATION_MESSAGES.PASSWORD_TOO_SHORT);
      } else if (err?.message?.includes('Please enter a valid email')) {
        setSubmissionError(VALIDATION_MESSAGES.INVALID_EMAIL_FORMAT);
      } else if (err?.message?.includes(VALIDATION_MESSAGES.FIX_FOLLOWING_ERRORS)) {
        setSubmissionError(err.message.replace(VALIDATION_MESSAGES.FIX_FOLLOWING_ERRORS, ''));
      } else if (err?.message?.includes(VALIDATION_MESSAGES.VALIDATION_FAILED_PREFIX)) {
        setSubmissionError(err.message.replace(VALIDATION_MESSAGES.VALIDATION_FAILED_PREFIX, ''));
      } else if (err?.code === '23505' || err?.message?.includes('duplicate key value violates unique constraint')) {
        if (err?.message?.includes('username')) {
          setSubmissionError(VALIDATION_MESSAGES.USERNAME_ALREADY_TAKEN);
          setValidationErrors(prev => ({
            ...prev,
            username: VALIDATION_MESSAGES.USERNAME_ALREADY_TAKEN_INLINE
          }));
        } else {
          const token = searchParams.get('token') || searchParams.get('transfer_token');
          if (token) {
            setSubmissionError(VALIDATION_MESSAGES.EMAIL_ALREADY_REGISTERED_FOR_CLAIM);
            setIsLogin(true); // Automatically switch to login mode
            setValidationErrors(prev => ({
              ...prev,
              email: VALIDATION_MESSAGES.EMAIL_ALREADY_REGISTERED_INLINE_LOGIN
            }));
          } else {
            setSubmissionError(VALIDATION_MESSAGES.EMAIL_ALREADY_REGISTERED);
            setValidationErrors(prev => ({
              ...prev,
              email: VALIDATION_MESSAGES.EMAIL_ALREADY_REGISTERED_INLINE
            }));
          }
        }
      } else if (err?.message?.includes('row-level security policy')) {
        setSubmissionError(VALIDATION_MESSAGES.AUTH_ISSUE);
      } else if (err?.message?.includes('For security purposes, you can only request this after')) {
        setSubmissionError(VALIDATION_MESSAGES.TOO_MANY_REQUESTS);
      } else if (err?.message?.includes('rate limit') || err?.message?.includes('too many requests')) {
        setSubmissionError(VALIDATION_MESSAGES.TOO_MANY_REQUESTS);
      } else if (err?.message?.includes(VALIDATION_MESSAGES.EMAIL_ALREADY_ASSOCIATED_WITH_CLAIMED_PROFILE)) {
        setSubmissionError(err.message);
      } else {
        // Log the error for debugging but don't crash the page
        console.error('Unhandled signup error:', error);
        setSubmissionError(isLogin ? VALIDATION_MESSAGES.SIGN_IN_ERROR : VALIDATION_MESSAGES.CREATE_ACCOUNT_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-white/50"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-gradient-to-r from-lime-green to-soft-teal rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <Check className="w-8 h-8 text-white" />
          </motion.div>

          <h2 className="text-2xl font-bold text-charcoal mb-4">Account Created Successfully!</h2>
          <p className="text-slate-gray mb-6">
            {isLogin ?
              `Welcome back! ${formData.role === 'cdmo' ? 'You can now complete your CDMO application.' : 'You can now explore our CDMO network and find potential manufacturing partners.'}` :
              `Welcome to Pharma Connect Navigator, ${formData.name}! ${formData.role === 'cdmo' ? 'You can now complete your CDMO application to join our partner network.' : 'You can now explore our CDMO network and find potential manufacturing partners.'}`
            }
            <br />
            <span className="text-sm text-electric-blue font-medium">
              {formData.role === 'cdmo' ? 'Redirecting you to the CDMO application...' : 'Redirecting you to explore our CDMO network...'}
            </span>
          </p>

          <Link to={formData.role === 'cdmo' ? '/cdmo-application' : '/matchmaking'}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
            >
              {formData.role === 'cdmo' ? 'Complete CDMO Application' : 'Explore CDMO Network'}
            </motion.button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const { strength: passwordStrength } = validatePassword(formData.password);
  const showPasswordRequirements = !isLogin && formData.password.length > 0;
  // The checklist already conveys length and character-class rules; the inline error stays
  // for what it cannot express (common passwords, sequential characters, excessive length).
  const meetsAllPasswordRules = Object.values(passwordStrength).every(Boolean);
  const showPasswordError =
    Boolean(validationErrors.password) && (!showPasswordRequirements || meetsAllPasswordRules);

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Header */}
      <div className="bg-gradient-to-br from-lime-green/20 to-soft-teal/15 backdrop-blur-sm border-b border-lime-green/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link to="/" className="inline-flex items-center text-electric-blue hover:text-deep-indigo transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center flex-wrap">
            <div className="w-10 h-10 bg-gradient-success rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg">
              <Rocket className="w-6 h-6 text-white" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-charcoal">Create Your Account</h1>
              <p className="text-sm sm:text-base text-slate-gray">
                {isLogin ? 'Sign in to access your account' : 'Join our platform to find and connect with top-tier CDMOs'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={handleSubmit}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/50"
        >
          <div className="p-6 sm:p-8">
            {/* Toggle between Sign Up and Login */}
            <div className="flex items-center justify-center mb-6 sm:mb-8">
              <div className="bg-light-gray rounded-xl p-1 flex">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setIsLogin(false);
                    setValidationErrors({});
                    setSubmissionError(null);
                  }}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 ${!isLogin
                    ? 'bg-white text-electric-blue shadow-md'
                    : 'text-slate-gray hover:text-charcoal'
                    }`}
                  aria-label="Sign Up"
                >
                  Sign Up
                </motion.button>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setIsLogin(true);
                    setValidationErrors({});
                    setSubmissionError(null);
                  }}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 ${isLogin
                    ? 'bg-white text-electric-blue shadow-md'
                    : 'text-slate-gray hover:text-charcoal'
                    }`}
                  aria-label="Sign In"
                >
                  Sign In
                </motion.button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Role Selection - Only for Sign Up */}
              {!isLogin && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="md:col-span-2 relative"
                >
                  <label htmlFor="role" className="block text-sm sm:text-base font-medium text-charcoal mb-4">
                    <Users className="inline w-4 h-4 mr-1" />
                    I am a *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <motion.label
                      whileHover={!disabledFields.role ? { scale: 1.02 } : {}}
                      whileTap={!disabledFields.role ? { scale: 0.98 } : {}}
                      className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${formData.role === 'startup'
                        ? SELECTED_TILE_CLASS
                        : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white/80'
                        } ${disabledFields.role ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value="startup"
                        checked={formData.role === 'startup'}
                        onChange={handleInputChange}
                        disabled={disabledFields.role}
                        className="sr-only"
                        id="startup-role"
                      />
                      <div className="flex items-center">
                        <Rocket className="w-5 h-5 mr-3 text-electric-blue" aria-hidden="true" />
                        <div>
                          <span className="font-semibold">Biotech/Pharma Professional</span>
                          <p className="text-sm text-slate-gray mt-1">Looking for manufacturing partners</p>
                        </div>
                      </div>
                    </motion.label>

                    <motion.label
                      whileHover={!disabledFields.role ? { scale: 1.02 } : {}}
                      whileTap={!disabledFields.role ? { scale: 0.98 } : {}}
                      className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${formData.role === 'cdmo'
                        ? SELECTED_TILE_CLASS
                        : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white/80'
                        } ${disabledFields.role ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value="cdmo"
                        checked={formData.role === 'cdmo'}
                        onChange={handleInputChange}
                        disabled={disabledFields.role}
                        className="sr-only"
                        id="cdmo-role"
                      />
                      <div className="flex items-center">
                        <Building className="w-5 h-5 mr-3 text-electric-blue" aria-hidden="true" />
                        <div>
                          <span className="font-semibold">CDMO Representative</span>
                          <p className="text-sm text-slate-gray mt-1">Offering manufacturing services</p>
                        </div>
                      </div>
                    </motion.label>
                  </div>
                </motion.div>
              )}

              {/* Name */}
              {!isLogin && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <label htmlFor="name" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    <User className="inline w-4 h-4 mr-1" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required={!isLogin}
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.name
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-gray/30'
                      }`}
                    placeholder="Enter your full name"
                    id="name"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                  )}
                </motion.div>
              )}

              {/* Email */}
              <motion.div
                whileHover={!(disabledFields.email && !isLogin) ? { scale: 1.02 } : {}}
                className={`relative ${isLogin ? 'md:col-span-2' : ''}`}
              >
                <label htmlFor="email" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  disabled={disabledFields.email && !isLogin}
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.email
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-slate-gray/30'
                    } ${(disabledFields.email && !isLogin) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  placeholder="Enter your email"
                  id="email"
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </motion.div>

              {/* Username */}
              {!isLogin && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <label htmlFor="username" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    <Users className="inline w-4 h-4 mr-1" />
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    required={!isLogin}
                    value={formData.username}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.username
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-gray/30'
                      }`}
                    placeholder="Choose a unique username"
                    id="username"
                  />
                  {validationErrors.username && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.username}</p>
                  )}
                </motion.div>
              )}

              {/* Password */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className={`relative ${isLogin ? 'md:col-span-2' : ''}`}
              >
                <label htmlFor="password" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                  <Lock className="inline w-4 h-4 mr-1" />
                  Password *
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.password
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-slate-gray/30'
                    }`}
                  placeholder="Enter your password"
                  minLength={8}
                  id="password"
                />
                {showPasswordError && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
                )}
                {showPasswordRequirements && (
                  <PasswordRequirements strength={passwordStrength} className="mt-2" />
                )}
              </motion.div>

              {/* Company */}
              {!isLogin && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <label htmlFor="company" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    <Building className="inline w-4 h-4 mr-1" />
                    Company *
                  </label>
                  <input
                    type="text"
                    name="company"
                    required={!isLogin}
                    value={formData.company}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.company
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-gray/30'
                      }`}
                    placeholder="Enter your company name"
                    id="company"
                  />
                  {validationErrors.company && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.company}</p>
                  )}
                </motion.div>
              )}

              {/* Website - Only for CDMO */}
              {!isLogin && formData.role === 'cdmo' && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <label htmlFor="website" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    <Globe className="inline w-4 h-4 mr-1" />
                    Company Website *
                  </label>
                  <input
                    type="url"
                    name="website"
                    required
                    value={formData.website}
                    disabled={disabledFields.website}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.website
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-gray/30'
                      } ${disabledFields.website
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : ''
                      }`}
                    placeholder="https://www.company.com"
                    id="website"
                  />
                  {validationErrors.website && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.website}</p>
                  )}
                </motion.div>
              )}

              {/* Job Role */}
              {!isLogin && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <label htmlFor="jobRole" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    <User className="inline w-4 h-4 mr-1" />
                    Job Title/Role *
                  </label>
                  <input
                    type="text"
                    name="jobRole"
                    required={!isLogin}
                    value={formData.jobRole}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.jobRole
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-gray/30'
                      }`}
                    placeholder="e.g., CMC Director, CEO, Head of Manufacturing"
                    id="jobRole"
                  />
                </motion.div>
              )}
            </div>

            {/* Development Stage */}
            {!isLogin && formData?.role === 'startup' && (
              <div className="mb-6 sm:mb-8">
                <label className="block text-sm sm:text-base font-medium text-charcoal mb-0">
                  <FlaskConical className="inline w-4 h-4 mr-1" />
                  Development Stage
                </label>
                <p className="text-sm text-slate-gray mb-2">Optional, select all that apply.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stages.map(stage => (
                    <motion.label
                      key={stage}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex items-center p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 min-h-[48px] ${formData.developmentStage.includes(stage)
                        ? SELECTED_TILE_CLASS
                        : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white/80'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.developmentStage.includes(stage)}
                        onChange={() => handleDevelopmentStageChange(stage)}
                        className="sr-only"
                        id={`developmentStage-${stage}`}
                      />
                      <span className="text-sm sm:text-base font-medium text-center w-full">{stage}</span>
                    </motion.label>
                  ))}
                </div>
              </div>
            )}

            {/* What you're looking for */}
            {!isLogin && formData?.role === 'startup' && (
              <div className="mb-6 sm:mb-8">
                <label htmlFor="lookingFor" className="block text-sm sm:text-base font-medium text-charcoal mb-0">
                  What type of CDMO support are you exploring?
                </label>
                <p className="text-sm text-slate-gray mb-2">Optional, select all that apply.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {services.map(service => (
                    <motion.label
                      key={service}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex items-center p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 min-h-[48px] ${formData?.lookingFor?.includes(service)
                        ? SELECTED_TILE_CLASS
                        : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white/80'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData?.lookingFor?.includes(service)}
                        onChange={() => handleCheckboxChange(service)}
                        className="sr-only"
                        id={`lookingFor-${service}`}
                      />
                      <span className="text-sm sm:text-base font-medium text-center w-full">{service}</span>
                    </motion.label>
                  ))}
                </div>
              </div>
            )}

            {/* Challenge */}
            {!isLogin && formData?.role === 'startup' && (
              <motion.div
                whileHover={{ scale: 1.01 }}
                className="mb-6 sm:mb-8"
              >
                <label htmlFor="challenge" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                  What's your biggest manufacturing challenge? (Optional)
                </label>
                <textarea
                  name="challenge"
                  value={formData.challenge}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 sm:py-4 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 resize-none bg-white/80 text-base"
                  placeholder="Tell us about your manufacturing challenges or specific requirements..."
                  id="challenge"
                />
              </motion.div>
            )}

            {/* Success Message for Registration */}
            {searchParams.get('message') === 'registration_success' && isLogin && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl"
                aria-live="polite"
                role="alert"
              >
                <div className="flex items-start">
                  <span className="w-5 h-5 text-green-600 mr-2 mt-0.5" >
                    <Check />
                  </span>
                  <p className="text-green-600 text-sm font-medium">
                    Registration successful! Please sign in to continue.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Already Claimed Warning */}
            {alreadyClaimed && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start space-x-3"
              >
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-semibold text-sm">
                    {submissionError?.includes('another user')
                      ? 'Account Already Claimed'
                      : VALIDATION_MESSAGES.PROFILE_ALREADY_CLAIMED_TITLE}
                  </h4>
                  <p className="text-amber-700 text-sm mt-1">
                    {submissionError || VALIDATION_MESSAGES.PROFILE_ALREADY_CLAIMED_MESSAGE}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Error Message */}
            {submissionError && !alreadyClaimed && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
                aria-live="polite"
                role="alert"
              >
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                  <p className="text-red-600 text-sm font-medium">{submissionError}</p>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-6 sm:px-8 py-4 sm:py-5 bg-gradient-success text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-base sm:text-lg min-h-[48px]"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isLogin ? 'Signing In...' : 'Creating Account...'}
                </div>
              ) : (
                <>
                  {isLogin ? (
                    <>
                      <LogIn className="w-5 h-5 mr-2 inline" aria-hidden="true" />
                      Sign In
                    </>
                  ) : (
                    'Create Account'
                  )}
                </>
              )}
            </motion.button>

            {/* Forgot Password Link for Login */}
            {isLogin && (
              <div className="text-center mt-4">
                <Link
                  to="/password-reset-request"
                  className="text-sm text-electric-blue hover:text-deep-indigo transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>
            )}
          </div>
        </motion.form>
      </div>
      {/* Message Modal */}
      <MessageModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type}
        onClose={() => setMessageModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default StartupSignup;
